import React, { useEffect, useMemo, useState } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HCMore from 'highcharts/highcharts-more';
import HCStreamgraph from 'highcharts/modules/streamgraph';
import HCSolidGauge from 'highcharts/modules/solid-gauge';
import HCPatternFill from 'highcharts/modules/pattern-fill';
import HCWordcloud from 'highcharts/modules/wordcloud';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, useLocation } from 'react-router-dom';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  createDashboardTask,
  fetchActivePatients,
  fetchAiInsights,
  fetchAiUsage,
  fetchAppointmentsRange,
  fetchAppointmentVolume,
  fetchBillingAlerts,
  fetchKpis,
  fetchLabTurnaround,
  fetchNoShowVsCompleted,
  fetchProviderWorkload,
  fetchRecentNotes,
  fetchRevenueTrends,
  fetchTasks,
  fetchTaskAssignees,
  fetchTelemedLogs,
  fetchTelemedUtilization,
  fetchTopDiagnoses,
  fetchUpcomingAppointments,
  type TaskAssigneeOption,
} from '../dashboard/liveProviders';
import {
  AppointmentRow,
  BillingAlertRow,
  ClinicalNoteRow,
  Kpi,
  TaskRow,
  TelemedLogRow,
  TimeSeries,
  StackedBarSeries,
  AiInsight,
  DateRange,
  TimeSeriesPoint,
} from '../dashboard/types';
import { cn } from '../utils/cn';
import InitialAvatar from '../components/InitialAvatar';
import { ArrowRight, Brain, CalendarClock, AlertTriangle, CalendarDays, CheckCircle2, Sparkles, Users, X, Settings2 } from 'lucide-react';
import { useI18n } from '../i18n/LanguageContext';
import { getOrgCurrency } from '../i18n/orgFormat';
import type { SupportedLanguage } from '../i18n/translations';
import { API_BASE } from '../config/env';
import { filterMvpDashboardWidgets } from '../config/mvpVisibility';

// Highcharts module init (once)
if (typeof Highcharts === 'object') {
  HCMore(Highcharts);
  HCStreamgraph(Highcharts);
  HCSolidGauge(Highcharts);
  HCPatternFill(Highcharts);
  HCWordcloud(Highcharts);
}

const palette = ['#0f172a', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#6366f1'];
const ResponsiveGridLayout = WidthProvider(Responsive);
type SizeKey = 'sm' | 'md' | 'lg';
const sizePresets: Record<SizeKey, { w: number; h: number }> = {
  sm: { w: 4, h: 10 },
  md: { w: 6, h: 12 },
  lg: { w: 8, h: 14 },
};
type LayoutItem = { i: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number; size?: SizeKey };

const defaultRange: DateRange = {
  start: new Date(new Date().setDate(new Date().getDate() - 14)).toISOString(),
  end: new Date().toISOString(),
};

type WidgetDef = {
  id: string;
  title: string;
  minW?: number;
  minH?: number;
  w?: number;
  h?: number;
  size?: SizeKey;
  render: () => React.ReactNode;
};

type ProviderDirectoryEntry = {
  id: string;
  name: string;
  avatarUrl?: string | null;
};

type DashboardState = {
  kpis: Kpi[];
  scheduleAppointments: AppointmentRow[];
  appointmentVolume?: TimeSeries;
  noShowVsCompleted?: StackedBarSeries;
  activePatients?: TimeSeries;
  telemedUtil?: TimeSeries;
  providerWorkload?: StackedBarSeries;
  aiUsage?: TimeSeries;
  revenueTrends?: TimeSeries;
  topDiagnoses?: Array<{ code: string; label: string; count: number }>;
  labTurnaround?: TimeSeries;
  upcoming: AppointmentRow[];
  notes: ClinicalNoteRow[];
  billingAlerts: BillingAlertRow[];
  telemedLogs: TelemedLogRow[];
  tasks: TaskRow[];
  aiInsights: AiInsight[];
  dashboardLayout: LayoutItem[];
};

type PhraseTranslator = (phrase: string) => string;

const localeByLanguage: Record<SupportedLanguage, string> = {
  en: 'en-US',
  es: 'es-ES',
  it: 'it-IT',
};

const dashboardEsOverrides: Record<string, string> = {
  Dashboard: 'Panel',
  'Operations Overview': 'Resumen operativo',
  'Good Morning': 'Buenos dias',
  'Good Afternoon': 'Buenas tardes',
  'Good Evening': 'Buenas noches',
  'Customize Dashboard': 'Personalizar panel',
  'Manage Widgets': 'Gestionar widgets',
  'Reset to Default': 'Restablecer por defecto',
  Done: 'Listo',
  Share: 'Compartir',
  'Add Task': 'Agregar tarea',
  'Failed to save layout': 'No se pudo guardar el diseno',
  'Saving...': 'Guardando...',
  'Revenue & Claims': 'Ingresos y reclamos',
  'Total Revenue': 'Ingreso total',
  Revenue: 'Ingresos',
  'Active Patients': 'Pacientes activos',
  'Active Patients Trend': 'Tendencia de pacientes activos',
  Patients: 'Pacientes',
  Count: 'Conteo',
  'Upcoming Appointments': 'Proximas citas',
  Patient: 'Paciente',
  Provider: 'Profesional',
  Type: 'Tipo',
  Start: 'Inicio',
  Status: 'Estado',
  'No-Show Rate': 'Tasa de ausencias',
  'No-Show Rate (Completed vs Missed)': 'Tasa de ausencias (completadas vs ausentes)',
  'No-show vs Completed': 'Ausencias vs completadas',
  'Appointment Volume': 'Volumen de citas',
  'Appointment Volume (Clinic vs Telemed)': 'Volumen de citas (clinica vs telemedicina)',
  Volume: 'Volumen',
  Visits: 'Visitas',
  'Telemed Utilization': 'Uso de telemedicina',
  'Telemed Utilization Gauge': 'Indicador de uso de telemedicina',
  'Recent Clinical Notes': 'Notas clinicas recientes',
  Created: 'Creado',
  'AI Assisted': 'Asistido por IA',
  Yes: 'Si',
  No: 'No',
  'AI Assistant': 'Asistente IA',
  'Provider Workload': 'Carga por profesional',
  'Provider Workload Stream': 'Flujo de carga por profesional',
  'Billing Alerts': 'Alertas de facturacion',
  Claim: 'Reclamo',
  Amount: 'Monto',
  Reason: 'Motivo',
  'Top Diagnoses': 'Diagnosticos principales',
  'Top Diagnoses Frequency': 'Frecuencia de diagnosticos principales',
  'AI Usage Trend': 'Tendencia de uso de IA',
  'AI Assistance Usage': 'Uso de asistencia IA',
  'AI Assistance': 'Asistencia IA',
  'Lab Turnaround': 'Tiempo de laboratorio',
  'Lab Turnaround Time': 'Tiempo de respuesta de laboratorio',
  Turnaround: 'Tiempo de respuesta',
  Days: 'Dias',
  'Tasks & Follow-ups': 'Tareas y seguimientos',
  Title: 'Titulo',
  Due: 'Vence',
  Owner: 'Responsable',
  Priority: 'Prioridad',
  'Telemed Session Logs': 'Registros de sesiones de telemedicina',
  Duration: 'Duracion',
  Quality: 'Calidad',
  Drops: 'Cortes',
  'Claims Donut': 'Donut de reclamos',
  'Claim Success vs Rejected (donut)': 'Reclamos exitosos vs rechazados (donut)',
  'Dx Pattern Pie': 'Pastel de patrones diagnosticos',
  'Diagnoses Share (pattern pie)': 'Participacion de diagnosticos (pastel)',
  'Word Cloud': 'Nube de palabras',
  'Clinical Topics Word Cloud': 'Nube de temas clinicos',
  'No data': 'Sin datos',
  Clinic: 'Clinica',
  Telemed: 'Telemedicina',
  Utilization: 'Uso',
  Completed: 'Completada',
  Claims: 'Reclamos',
  Success: 'Exitosos',
  Rejected: 'Rechazados',
  Frequency: 'Frecuencia',
  Diagnoses: 'Diagnosticos',
  'Dx share': 'Participacion de diagnosticos',
  Hypertension: 'Hipertension',
  Televisit: 'Teleconsulta',
  'AI Notes': 'Notas de IA',
  Labs: 'Laboratorios',
  Hours: 'Horas',
  'Claims Submitted': 'Reclamos enviados',
  'Claims Rejected': 'Reclamos rechazados',
  'Revenue (30d)': 'Ingresos (30 dias)',
  'Appointments (30d)': 'Citas (30 dias)',
  'No-Shows (30d)': 'Ausencias (30 dias)',
  'Patients today': 'Pacientes hoy',
  'Telemedicine visits': 'Visitas de telemedicina',
  'AI note usage': 'Uso de notas IA',
  'Avg appt duration': 'Duracion promedio de cita',
  'Revenue today': 'Ingresos de hoy',
  'Revenue this month': 'Ingresos de este mes',
  'Pending lab results': 'Resultados de laboratorio pendientes',
  'Claims rejected': 'Reclamos rechazados',
  'No-show rate': 'Tasa de ausencias',
  Scheduled: 'Programada',
  'Checked-in': 'Registrada',
  'No-show': 'Ausente',
  Open: 'Abierta',
  'In Progress': 'En progreso',
  Denied: 'Denegado',
  'Coding Error': 'Error de codificacion',
  ORDERED: 'Ordenado',
  IN_PROGRESS: 'En progreso',
  SCHEDULED: 'Programada',
  CHECKED_IN: 'Registrada',
  NO_SHOW: 'Ausente',
  'AI Insights': 'Insights IA',
  'Predictive no-show alerts': 'Alertas predictivas de ausencias',
  risk: 'riesgo',
  'No alerts': 'Sin alertas',
  'High-risk patients': 'Pacientes de alto riesgo',
  'Daily summary': 'Resumen diario',
  'Summary not available.': 'Resumen no disponible.',
  'AI Overview': 'Resumen IA',
  'Rapid clinic status snapshot for today': 'Resumen rapido del estado de la clinica para hoy',
  'AI Daily Insight': 'Insight diario de IA',
  'High Load': 'Carga alta',
  'Moderate Load': 'Carga moderada',
  'Stable Load': 'Carga estable',
  'vs prior': 'vs periodo previo',
  'Open tasks': 'Tareas abiertas',
  'In progress': 'En progreso',
  'Task Name': 'Nombre de la tarea',
  Description: 'Descripcion',
  Assign: 'Asignar',
  Me: 'Yo',
  'My Projects': 'Mis proyectos',
  'This Week': 'Esta semana',
  'See All': 'Ver todo',
  Schedule: 'Agenda',
  'Daily Load': 'Carga diaria',
  Booked: 'Reservadas',
  appointments: 'citas',
  'No appointments on selected date': 'No hay citas en la fecha seleccionada',
  'AI Summary for Today': 'Resumen de IA para hoy',
  Live: 'En vivo',
  'No-show alerts': 'Alertas de ausencias',
  'Pending labs': 'Laboratorios pendientes',
  'Select date': 'Seleccionar fecha',
  Today: 'Hoy',
  'My schedule': 'Mi agenda',
  Low: 'Baja',
  Med: 'Media',
  High: 'Alta',
};

const dashboardItOverrides: Record<string, string> = {
  Dashboard: 'Dashboard',
  'Operations Overview': 'Panoramica delle Operazioni',
  'Good Morning': 'Buongiorno',
  'Good Afternoon': 'Buon pomeriggio',
  'Good Evening': 'Buonasera',
  'Customize Dashboard': 'Personalizza Dashboard',
  'Manage Widgets': 'Gestisci Widget',
  'Reset to Default': 'Ripristina Predefinito',
  Done: 'Fatto',
  Share: 'Condividi',
  'Add Task': 'Aggiungi attività',
  'Failed to save layout': 'Impossibile salvare il layout',
  'Saving...': 'Salvataggio in corso...',
  'Revenue & Claims': 'Entrate e Reclami',
  'Total Revenue': 'Entrate Totali',
  Revenue: 'Entrate',
  'Active Patients': 'Pazienti Attivi',
  'Active Patients Trend': 'Tendenza Pazienti Attivi',
  Patients: 'Pazienti',
  Count: 'Conteggio',
  'Upcoming Appointments': 'Prossimi Appuntamenti',
  Patient: 'Paziente',
  Provider: 'Fornitore',
  Type: 'Tipo',
  Start: 'Inizio',
  Status: 'Stato',
  'No-Show Rate': 'Tasso di Mancate Presentazioni',
  'No-Show Rate (Completed vs Missed)': 'Tasso di Mancata Presentazione (Completate vs Perse)',
  'No-show vs Completed': 'Mancata Presentazione vs Completate',
  'Appointment Volume': 'Volume degli Appuntamenti',
  'Appointment Volume (Clinic vs Telemed)': 'Volume Appuntamenti (Clinica vs Telemedicina)',
  Volume: 'Volume',
  Visits: 'Visite',
  'Telemed Utilization': 'Utilizzo Telemedicina',
  'Telemed Utilization Gauge': 'Indicatore Utilizzo Telemedicina',
  'Recent Clinical Notes': 'Note Cliniche Recenti',
  Created: 'Creato',
  'AI Assisted': 'Assistito dall\'AI',
  Yes: 'Sì',
  No: 'No',
  'AI Assistant': 'Assistente AI',
  'Provider Workload': 'Carico di Lavoro del Fornitore',
  'Provider Workload Stream': 'Flusso Carico Fornitore',
  'Billing Alerts': 'Avvisi di Fatturazione',
  Claim: 'Reclamo',
  Amount: 'Importo',
  Reason: 'Motivo',
  'Top Diagnoses': 'Diagnosi Principali',
  'Top Diagnoses Frequency': 'Frequenza delle Diagnosi Principali',
  'AI Usage Trend': 'Tendenza Utilizzo AI',
  'AI Assistance Usage': 'Utilizzo Assistenza AI',
  'AI Assistance': 'Assistenza AI',
  'Lab Turnaround': 'Tempi di Laboratorio',
  'Lab Turnaround Time': 'Tempo di Risposta Laboratorio',
  Turnaround: 'Risposta',
  Days: 'Giorni',
  'Tasks & Follow-ups': 'Attività e Follow-up',
  Title: 'Titolo',
  Due: 'Scadenza',
  Owner: 'Responsabile',
  Priority: 'Priorità',
  'Telemed Session Logs': 'Registro Sessioni Telemedicina',
  Duration: 'Durata',
  Quality: 'Qualità',
  Drops: 'Disconnessioni',
  'Claims Donut': 'Grafico a Ciambella dei Reclami',
  'Claim Success vs Rejected (donut)': 'Reclami Riusciti vs Rifiutati',
  'Dx Pattern Pie': 'Grafico a Torta dei Modelli Diagnostici',
  'Diagnoses Share (pattern pie)': 'Quota de Diagnosi',
  'Word Cloud': 'Nuvola di Parole',
  'Clinical Topics Word Cloud': 'Nuvola di Temi Clinici',
  'No data': 'Nessun dato',
  Clinic: 'Clinica',
  Telemed: 'Telemedicina',
  Utilization: 'Utilizzo',
  Completed: 'Completata',
  Claims: 'Reclami',
  Success: 'Riusciti',
  Rejected: 'Rifiutati',
  Frequency: 'Frequenza',
  Diagnoses: 'Diagnosi',
  'Dx share': 'Quota Diagnosi',
  Hypertension: 'Ipertensione',
  Televisit: 'Televisita',
  'AI Notes': 'Note AI',
  Labs: 'Laboratori',
  Hours: 'Ore',
  'Claims Submitted': 'Reclami Inviati',
  'Claims Rejected': 'Reclami Rifiutati',
  'Revenue (30d)': 'Entrate (30g)',
  'Appointments (30d)': 'Appuntamenti (30g)',
  'No-Shows (30d)': 'Mancate Presentazioni (30g)',
  'Patients today': 'Pazienti oggi',
  'Telemedicine visits': 'Visite telemedicina',
  'AI note usage': 'Uso delle note AI',
  'Avg appt duration': 'Durata media dell\'appuntamento',
  'Revenue today': 'Entrate oggi',
  'Revenue this month': 'Entrate di questo mese',
  'Pending lab results': 'Risultati di laboratorio in sospeso',
  'Claims rejected': 'Reclami rifiutati',
  'No-show rate': 'Tasso di mancate presentazioni',
  Scheduled: 'Programmata',
  'Checked-in': 'Registrata',
  'No-show': 'Mancata presentazione',
  Open: 'Aperta',
  'In Progress': 'In corso',
  Denied: 'Negata',
  'Coding Error': 'Errore di Codifica',
  ORDERED: 'Ordinato',
  IN_PROGRESS: 'In corso',
  SCHEDULED: 'Programmata',
  CHECKED_IN: 'Registrata',
  NO_SHOW: 'Mancata presentazione',
  'AI Insights': 'Informazioni AI',
  'Predictive no-show alerts': 'Avvisi predittivi di mancata presentazione',
  risk: 'rischio',
  'No alerts': 'Nessun avviso',
  'High-risk patients': 'Pazienti ad alto rischio',
  'Daily summary': 'Riepilogo giornaliero',
  'Summary not available.': 'Riepilogo non disponibile.',
  'AI Overview': 'Panoramica AI',
  'Rapid clinic status snapshot for today': 'Panoramica rapida dello stato della clinica di oggi',
  'AI Daily Insight': 'Insight Giornaliero AI',
  'High Load': 'Carico Alto',
  'Moderate Load': 'Carico Moderato',
  'Stable Load': 'Carico Stabile',
  'vs prior': 'vs precedente',
  'Open tasks': 'Attività aperte',
  'In progress': 'In corso',
  'Task Name': 'Nome attività',
  Description: 'Descrizione',
  Assign: 'Assegna',
  Me: 'Io',
  'My Projects': 'I miei progetti',
  'This Week': 'Questa settimana',
  'See All': 'Vedi tutto',
  Schedule: 'Agenda',
  'Daily Load': 'Carico giornaliero',
  Booked: 'Prenotato',
  appointments: 'appuntamenti',
  'No appointments on selected date': 'Nessun appuntamento nella data selezionata',
  'AI Summary for Today': 'Riepilogo AI di oggi',
  Live: 'Live',
  'No-show alerts': 'Avvisi di mancata presentazione',
  'Pending labs': 'Laboratori in sospeso',
  'Select date': 'Seleziona data',
  Today: 'Oggi',
  'My schedule': 'La mia agenda',
  Low: 'Bassa',
  Med: 'Media',
  High: 'Alta',
};

function getLocale(language: SupportedLanguage) {
  return localeByLanguage[language] ?? 'en-US';
}

function toLocalDayKey(value: Date) {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseSafeDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseDayKey(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function createDashboardTranslator(language: SupportedLanguage, tr: PhraseTranslator): PhraseTranslator {
  if (language === 'en') return tr;

  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;

    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) {
      return phrase.replace(normalized, globalTranslation);
    }

    if (language === 'es' || language === 'it') {
      const isIt = language === 'it';
      const direct = isIt ? dashboardItOverrides[normalized] : dashboardEsOverrides[normalized];
      if (direct) return phrase.replace(normalized, direct);

      const dayMatch = normalized.match(/^Day (\d+)$/i);
      if (dayMatch) return phrase.replace(normalized, isIt ? `Giorno ${dayMatch[1]}` : `Dia ${dayMatch[1]}`);

      const minutesMatch = normalized.match(/^(\d+)\s*min$/i);
      if (minutesMatch) return phrase.replace(normalized, `${minutesMatch[1]} min`);

      const orderMatch = normalized.match(/^Order:\s*(.+)$/i);
      if (orderMatch) return phrase.replace(normalized, isIt ? `Ordine: ${orderMatch[1]}` : `Orden: ${orderMatch[1]}`);
    }

    return phrase;
  };
}

export default function Dashboard() {
  const { language, tr } = useI18n();
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const location = useLocation();
  const td = useMemo(() => createDashboardTranslator(language, tr), [language, tr]);
  const [providerDirectory, setProviderDirectory] = useState<ProviderDirectoryEntry[]>([]);
  const [data, setData] = useState<DashboardState>({
    kpis: [],
    scheduleAppointments: [],
    upcoming: [],
    notes: [],
    billingAlerts: [],
    telemedLogs: [],
    tasks: [],
    aiInsights: [],
    dashboardLayout: [],
  });

  const [layout, setLayout] = useState<LayoutItem[]>([]);
  const [visibleWidgets, setVisibleWidgets] = useState<string[]>([]);
  const [savingLayout, setSavingLayout] = useState(false);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskSubmitError, setTaskSubmitError] = useState<string | null>(null);
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [assignees, setAssignees] = useState<TaskAssigneeOption[]>([]);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    due: '',
    assigneeId: '',
    priority: 'Med' as 'Low' | 'Med' | 'High',
  });
  const defs = useMemo(
    () => filterMvpDashboardWidgets(widgetDefs(data, td, language, providerDirectory)),
    [data, td, language, providerDirectory],
  );

  useEffect(() => {
    async function load() {
      let token: string | undefined;
      try {
        token = await getAccessTokenSilently({
          authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
        });
      } catch (err) {
        // Token might fail if login required, proceed without token (backend might restrict info)
      }

      const safe = async <T,>(loader: Promise<T>, fallback: T): Promise<T> => {
        try {
          return await loader;
        } catch {
          return fallback;
        }
      };
      const loadProvidersDirectory = async (): Promise<ProviderDirectoryEntry[]> => {
        if (!token) return [];
        const res = await fetch(`${API_BASE}/providers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return [];
        const payload = await res.json();
        return (Array.isArray(payload) ? payload : [])
          .map((provider: any) => {
            const id = typeof provider?.userId === 'string' ? provider.userId.trim() : '';
            const name = `${provider?.firstName ?? ''} ${provider?.lastName ?? ''}`.trim() || 'Provider';
            if (!id) return null;
            return {
              id,
              name,
              avatarUrl: typeof provider?.avatarUrl === 'string' && provider.avatarUrl.trim() ? provider.avatarUrl : null,
            } satisfies ProviderDirectoryEntry;
          })
          .filter((provider: ProviderDirectoryEntry | null): provider is ProviderDirectoryEntry => Boolean(provider));
      };

      const [
        kpis,
        scheduleAppointments,
        appointmentVolume,
        noShowVsCompleted,
        activePatients,
        telemedUtil,
        providerWorkload,
        aiUsage,
        revenueTrends,
        topDiagnoses,
        labTurnaround,
        upcoming,
        notes,
        billingAlerts,
        telemedLogs,
        tasks,
        aiInsights,
        storedLayout,
        orgAssignees,
        providersDirectory,
      ] = await Promise.all([
        safe(fetchKpis(token), []),
        safe(
          fetchAppointmentsRange(
            {
              start: (() => {
                const value = new Date();
                value.setHours(0, 0, 0, 0);
                return value.toISOString();
              })(),
              end: (() => {
                const value = new Date();
                value.setDate(value.getDate() + 7);
                value.setHours(23, 59, 59, 999);
                return value.toISOString();
              })(),
            },
            token,
          ),
          [],
        ),
        safe(fetchAppointmentVolume(defaultRange, token), { title: 'Appointment volume', points: [] }),
        safe(fetchNoShowVsCompleted(defaultRange, token), { label: 'Completed', buckets: [] }),
        safe(fetchActivePatients(defaultRange, token), { title: 'Active patients', points: [] }),
        safe(fetchTelemedUtilization(defaultRange, token), { title: 'Telemedicine utilization', points: [] }),
        safe(fetchProviderWorkload(defaultRange, token), { label: 'No data', buckets: [] }),
        safe(fetchAiUsage(defaultRange, token), { title: 'AI usage', points: [] }),
        safe(fetchRevenueTrends(defaultRange, token), { title: 'Revenue trends', points: [] }),
        safe(fetchTopDiagnoses(token), []),
        safe(fetchLabTurnaround(defaultRange, token), { title: 'Lab turnaround', points: [] }),
        safe(fetchUpcomingAppointments(token), []),
        safe(fetchRecentNotes(token), []),
        safe(fetchBillingAlerts(token), []),
        safe(fetchTelemedLogs(token), []),
        safe(token ? fetchTasks(token) : Promise.resolve([]), []),
        safe(fetchAiInsights(token), []),
        safe(loadDashboardLayout(token), []),
        safe(token ? fetchTaskAssignees(token) : Promise.resolve([]), []),
        safe(loadProvidersDirectory(), []),
      ]);
      const loadedData: DashboardState = {
        kpis,
        scheduleAppointments,
        appointmentVolume,
        noShowVsCompleted,
        activePatients,
        telemedUtil,
        providerWorkload,
        aiUsage,
        revenueTrends,
        topDiagnoses,
        labTurnaround,
        upcoming,
        notes,
        billingAlerts,
        telemedLogs,
        tasks,
        aiInsights,
        dashboardLayout: storedLayout,
      };
      setData(loadedData);
      setAssignees(orgAssignees);
      setProviderDirectory(providersDirectory);

      // Initial visibility: if stored layout exists, use it to determine visibility. 
      // Else default to all.
      const defs = filterMvpDashboardWidgets(widgetDefs(loadedData, td, language, providersDirectory));
      const allIds = defs.map((w) => w.id);
      const requiredIds = ['providerDayDispatch'].filter((id) => allIds.includes(id));

      if (storedLayout.length > 0) {
        const storedIds = storedLayout.map((l) => l.i).filter((id) => allIds.includes(id));
        if (storedIds.length > 0) {
          const visibleIds = Array.from(new Set([...storedIds, ...requiredIds]));

          setVisibleWidgets(visibleIds);
          if (location.pathname === '/command-center') {
            setLayout(buildDefaultLayout(visibleIds));
          } else {
            const baseLayout = storedLayout.filter((l) => storedIds.includes(l.i));
            const extendedLayout = [...baseLayout];

            requiredIds.forEach((requiredId) => {
              if (extendedLayout.some((item) => item.i === requiredId)) return;
              const def = defs.find((widget) => widget.id === requiredId);
              const nextY = extendedLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
              extendedLayout.push({
                i: requiredId,
                x: 0,
                y: nextY,
                w: def?.w ?? 12,
                h: def?.h ?? 10,
                minW: def?.minW ?? 4,
                minH: def?.minH ?? 8,
                size: def?.size ?? 'md',
              });
            });

            setLayout(extendedLayout);
          }
        } else {
          setVisibleWidgets(allIds);
          setLayout(buildDefaultLayout(allIds));
        }
      } else {
        setVisibleWidgets(allIds);
        setLayout(buildDefaultLayout(allIds));
      }
    }
    load();
  }, []);

  async function loadDashboardLayout(token?: string): Promise<LayoutItem[]> {
    try {
      if (!token) {
        try {
          token = await getAccessTokenSilently({
            authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
          });
        } catch {
          // Fall through to the unauthenticated settings request below.
        }
      }
      const res = await fetch(`${API_BASE}/settings`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Layout load failed ${res.status}`);
      const data = await res.json();
      return data.dashboard ?? [];
    } catch (err: any) {
      if (err?.error === 'login_required') {
        await loginWithRedirect({ appState: { returnTo: window.location.pathname } });
      }
      return [];
    }
  }

  async function saveLayout() {
    setSavingLayout(true);
    setLayoutError(null);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
      });
      const res = await fetch(`${API_BASE}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // We save the CURRENT layout (which only contains visible items)
        body: JSON.stringify({ dashboard: layout }),
      });
      if (!res.ok) throw new Error(`Save failed ${res.status}`);
    } catch (err: any) {
      setLayoutError(err.message || td('Failed to save layout'));
    } finally {
      setSavingLayout(false);
    }
  }

  async function openTaskModal() {
    setTaskSubmitError(null);
    setShowTaskModal(true);
    if (assignees.length) return;
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
      });
      const users = await fetchTaskAssignees(token);
      setAssignees(users);
    } catch (err: any) {
      setTaskSubmitError(err?.message ? td(err.message) : td('Unable to load assignees.'));
    }
  }

  async function submitTask(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!taskForm.title.trim()) {
      setTaskSubmitError(td('Task title is required.'));
      return;
    }
    setTaskSubmitting(true);
    setTaskSubmitError(null);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
      });
      const created = await createDashboardTask(
        {
          title: taskForm.title.trim(),
          description: taskForm.description.trim() || undefined,
          due: taskForm.due ? new Date(taskForm.due).toISOString() : undefined,
          assigneeId: taskForm.assigneeId || undefined,
          priority: taskForm.priority,
        },
        token,
      );
      setData((prev) => ({ ...prev, tasks: [created, ...prev.tasks] }));
      setTaskForm({ title: '', description: '', due: '', assigneeId: '', priority: 'Med' });
      setShowTaskModal(false);
    } catch (err: any) {
      setTaskSubmitError(err?.message ? td(err.message) : td('Unable to create task.'));
    } finally {
      setTaskSubmitting(false);
    }
  }

  function buildDefaultLayout(visibleIds: string[]): LayoutItem[] {
    if (location.pathname === '/command-center') {
      return buildCommandCenterLayoutFromDefs(defs, visibleIds);
    }
    return buildDefaultLayoutFromDefs(defs, visibleIds);
  }

  function normalizeLayoutHeights(items: LayoutItem[]): LayoutItem[] {
    return items.map((l) => {
      const snapped = snapToPreset(l.w, l.h, l.size);
      const clamped = {
        ...l,
        w: Math.max(sizePresets.sm.w, snapped.w),
        h: Math.max(sizePresets.sm.h, snapped.h),
        size: snapped.size,
        minW: sizePresets.sm.w,
        minH: sizePresets.sm.h,
      };
      // Ensure x stays within bounds for 12-col grid
      clamped.x = Math.min(Math.max(clamped.x, 0), 12 - clamped.w);
      return clamped;
    });
  }

  function snapToPreset(w: number, h: number, current?: SizeKey) {
    let chosen: SizeKey = current ?? 'md';
    let bestDiff = Infinity;
    (Object.keys(sizePresets) as SizeKey[]).forEach((key) => {
      const preset = sizePresets[key];
      const diff = Math.abs(preset.w - w) + Math.abs(preset.h - h);
      if (diff < bestDiff) {
        bestDiff = diff;
        chosen = key;
      }
    });
    return { ...sizePresets[chosen], size: chosen };
  }

  const locale = getLocale(language);
  const now = new Date();
  const currentHour = now.getHours();
  const displayName = (user?.given_name || user?.name || 'Clinician').split(' ')[0];
  const greetingText =
    currentHour < 12 ? td('Good Morning') : currentHour < 18 ? td('Good Afternoon') : td('Good Evening');
  const longDate = now.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  const todayKey = toLocalDayKey(now);
  const todayAppointments = useMemo(() => {
    return data.scheduleAppointments
      .filter((appointment) => {
        const start = parseSafeDate(appointment.start);
        return start ? toLocalDayKey(start) === todayKey : false;
      })
      .sort((left, right) => (parseSafeDate(left.start)?.getTime() ?? 0) - (parseSafeDate(right.start)?.getTime() ?? 0));
  }, [data.scheduleAppointments, todayKey]);
  const checkedInCount = todayAppointments.filter((appointment) => appointment.status === 'Checked-in').length;
  const completedCount = todayAppointments.filter((appointment) => appointment.status === 'Completed').length;
  const needsAttentionCount = todayAppointments.filter((appointment) => appointment.status === 'No-show' || appointment.status === 'Cancelled').length;
  const homeChartIds = ['revenue', 'apptVolume', 'activePatients', 'noShow'];

  useEffect(() => {
    if (location.pathname !== '/command-center') return;
    if (visibleWidgets.length > 0) return;
    const allIds = defs.map((w) => w.id);
    setVisibleWidgets(allIds);
    setLayout(buildDefaultLayout(allIds));
  }, [location.pathname, visibleWidgets.length, defs]);

  if (location.pathname === '/command-center') {
    return (
      <div className="space-y-6">
        <header className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">{td('Dashboard')}</p>
            <h1 className="text-2xl font-semibold text-ink">{td('Operations Overview')}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-ink transition-colors"
              onClick={() => setShowEditModal(true)}
              title={td('Customize Dashboard')}
            >
              <Settings2 size={18} />
            </button>
            {layoutError && <span className="text-xs text-rose-600">{layoutError}</span>}
          </div>
        </header>

        {showEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-5xl rounded-xl bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-bold mb-4">{td('Manage Widgets')}</h3>
              <div className="mb-6 grid max-h-[60vh] grid-cols-1 gap-3 overflow-y-auto md:grid-cols-2 xl:grid-cols-3">
                {defs.map(w => (
                  <label key={w.id} className="flex h-16 items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={visibleWidgets.includes(w.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const newVis = [...visibleWidgets, w.id];
                          setVisibleWidgets(newVis);
                          setLayout(buildDefaultLayout(newVis));
                        } else {
                          const newVis = visibleWidgets.filter(id => id !== w.id);
                          setVisibleWidgets(newVis);
                          setLayout(prev => prev.filter(l => l.i !== w.id));
                        }
                      }}
                      className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm font-medium">{w.title}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    const allIds = defs.map(w => w.id);
                    setVisibleWidgets(allIds);
                    setLayout(buildDefaultLayout(allIds));
                  }}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
                >
                  {td('Reset to Default')}
                </button>
                <button
                  onClick={async () => {
                    await saveLayout();
                    setShowEditModal(false);
                  }}
                  disabled={savingLayout}
                  className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-slate-800"
                >
                  {savingLayout ? td('Saving...') : td('Done')}
                </button>
              </div>
            </div>
          </div>
        )}

        <AiOverviewCard data={data} translate={td} language={language} />

        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {data.kpis.map((kpi) => (
            <KpiCard key={kpi.id} kpi={kpi} translate={td} language={language} />
          ))}
        </section>

        <ResponsiveGridLayout
          className="layout"
          rowHeight={40}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4 }}
          layouts={{ lg: layout, md: layout, sm: layout, xs: layout }}
          onLayoutChange={(newLayout) => setLayout(newLayout as LayoutItem[])}
          isDraggable={false}
          isResizable={false}
          isBounded
          preventCollision={false}
          margin={[12, 12]}
          containerPadding={[0, 0]}
          compactType="vertical"
        >
          {defs.filter(w => visibleWidgets.includes(w.id)).map((w) => (
            <div
              key={w.id}
              data-grid={
                layout.find((l) => l.i === w.id) ?? {
                  w: w.w ?? 6,
                  h: w.h ?? 10,
                  x: 0,
                  y: Infinity,
                }
              }
            >
              {w.render()}
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500 capitalize">{longDate}</p>
          <h1 className="mt-1 text-3xl font-semibold text-ink">{`${greetingText}! ${displayName},`}</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">Today at a glance: schedule, check-ins, completed visits, and trends.</p>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <HomeMetricCard
          label={td("Today's appointments")}
          value={todayAppointments.length.toLocaleString(locale)}
          detail={td('Booked for today')}
          icon={<CalendarDays size={18} />}
          tone="sky"
        />
        <HomeMetricCard
          label={td('Checked in')}
          value={checkedInCount.toLocaleString(locale)}
          detail={td('Patients currently in the clinic')}
          icon={<Users size={18} />}
          tone="emerald"
        />
        <HomeMetricCard
          label={td('Completed visits')}
          value={completedCount.toLocaleString(locale)}
          detail={td('Finished today')}
          icon={<CheckCircle2 size={18} />}
          tone="slate"
        />
        <HomeMetricCard
          label={td('Needs attention')}
          value={needsAttentionCount.toLocaleString(locale)}
          detail={needsAttentionCount ? td('Cancelled or missed today') : td('No missed visits today')}
          icon={<AlertTriangle size={18} />}
          tone={needsAttentionCount ? 'amber' : 'emerald'}
        />
      </section>

      <section className="grid gap-3 xl:grid-cols-2">
        {defs.filter((widget) => homeChartIds.includes(widget.id)).map((widget) => <React.Fragment key={widget.id}>{widget.render()}</React.Fragment>)}
      </section>

      <section className="grid gap-3 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <HomeTodayScheduleCard appointments={todayAppointments} translate={td} language={language} />
        <HomeProviderLoadCard appointments={todayAppointments} translate={td} language={language} />
      </section>

      {showTaskModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ink">{td('Add Task')}</h3>
              <button
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
                onClick={() => setShowTaskModal(false)}
                aria-label={td('Close')}
              >
                <X size={16} />
              </button>
            </div>
            <form className="space-y-3" onSubmit={submitTask}>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">{td('Task Name')}</label>
                <input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm((prev) => ({ ...prev, title: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  placeholder={td('Task Name')}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">{td('Description')}</label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="min-h-[88px] w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  placeholder={td('Description')}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">{td('Assign')}</label>
                  <select
                    value={taskForm.assigneeId}
                    onChange={(e) => setTaskForm((prev) => ({ ...prev, assigneeId: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">{td('Me')}</option>
                    {assignees.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">{td('Due')}</label>
                  <input
                    type="date"
                    value={taskForm.due}
                    onChange={(e) => setTaskForm((prev) => ({ ...prev, due: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">{td('Priority')}</label>
                <div className="flex gap-2">
                  {(['Low', 'Med', 'High'] as const).map((priority) => (
                    <button
                      key={priority}
                      type="button"
                      onClick={() => setTaskForm((prev) => ({ ...prev, priority }))}
                      className={cn(
                        'rounded-full border px-3 py-1 text-xs font-semibold',
                        taskForm.priority === priority ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-700',
                      )}
                    >
                      {td(priority)}
                    </button>
                  ))}
                </div>
              </div>
              {taskSubmitError && <p className="text-xs text-rose-600">{taskSubmitError}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="rounded-xl border border-slate-300 px-4 py-2 text-sm" onClick={() => setShowTaskModal(false)}>
                  {td('Cancel')}
                </button>
                <button type="submit" disabled={taskSubmitting} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                  {taskSubmitting ? td('Saving...') : td('Add Task')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {layoutError && <p className="text-xs text-rose-600">{layoutError}</p>}
    </div>
  );
}

function DashboardProjectsCard({
  data,
  translate,
  language,
}: {
  data: DashboardState;
  translate: PhraseTranslator;
  language: SupportedLanguage;
}) {
  const locale = getLocale(language);
  const projectRows = data.tasks.slice(0, 5);
  return (
    <section className="card overflow-hidden border border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <p className="text-2xl font-semibold text-ink">{translate('My Projects')}</p>
          <span className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
            {translate('This Week')}
          </span>
        </div>
        <button className="rounded-full bg-slate-100 px-4 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200">{translate('See All')}</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
              <th className="px-4 py-3 font-medium">{translate('Task Name')}</th>
              <th className="px-4 py-3 font-medium">{translate('Due')}</th>
              <th className="px-4 py-3 font-medium">{translate('Assign')}</th>
              <th className="px-4 py-3 font-medium">{translate('Status')}</th>
            </tr>
          </thead>
          <tbody>
            {projectRows.map((task) => (
              <tr key={task.id} className="border-b border-slate-100 text-slate-700">
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-800">{task.title}</p>
                  <p className="mt-1 text-xs text-slate-500">{translate('Priority')}: {translate(task.priority)}</p>
                </td>
                <td className="px-4 py-3 text-slate-600">{new Date(task.due).toLocaleDateString(locale, { month: 'short', day: 'numeric' })}</td>
                <td className="px-4 py-3 font-medium text-slate-800">{task.owner?.name || '--'}</td>
                <td className="px-4 py-3"><StatusBadge status={task.status} translate={translate} /></td>
              </tr>
            ))}
            {!projectRows.length && (
              <tr>
                <td className="px-4 py-4 text-slate-500" colSpan={4}>{translate('No data')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DashboardScheduleCard({
  data,
  translate,
  language,
}: {
  data: DashboardState;
  translate: PhraseTranslator;
  language: SupportedLanguage;
}) {
  const locale = getLocale(language);
  const today = useMemo(() => new Date(), []);
  const [selectedDayKey, setSelectedDayKey] = useState(() => toLocalDayKey(today));
  const dayEntries = useMemo(() => {
    return Array.from({ length: 7 }).map((_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() + index);
      return date;
    });
  }, [today]);

  const appointments = data.scheduleAppointments
    .filter((appointment) => toLocalDayKey(new Date(appointment.start)) === selectedDayKey)
    .slice(0, 6);

  return (
    <section className="card border border-slate-200 p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-3xl font-semibold text-ink">{translate('Schedule')}</p>
        <CalendarDays size={16} className="text-slate-400" />
      </div>
      <div className="mb-4 grid grid-cols-7 gap-1 rounded-xl border border-slate-200 bg-slate-50 p-2">
        {dayEntries.map((date) => {
          const dayKey = toLocalDayKey(date);
          const isSelected = dayKey === selectedDayKey;
          return (
            <button
              key={date.toISOString()}
              type="button"
              onClick={() => setSelectedDayKey(dayKey)}
              className={cn(
                'rounded-lg px-1 py-2 text-center transition-colors',
                isSelected ? 'bg-fuchsia-200' : 'bg-transparent hover:bg-slate-100',
              )}
            >
              <p className="text-[11px] uppercase tracking-wide text-slate-500">
                {date.toLocaleDateString(locale, { weekday: 'short' })}
              </p>
              <p className="text-sm font-semibold text-slate-800">{date.getDate()}</p>
            </button>
          );
        })}
      </div>
      <div className="space-y-3">
        {appointments.map((appointment) => (
          <div key={appointment.id} className="rounded-xl border border-slate-200 px-3 py-2">
            <p className="text-sm font-semibold text-slate-800">{appointment.patient}</p>
            <p className="mt-1 text-xs text-slate-500">
              {(() => {
                const start = parseSafeDate(appointment.start);
                const end = parseSafeDate(appointment.end);
                const endOrFallback = end || (start ? new Date(start.getTime() + 30 * 60 * 1000) : null);
                const startLabel = start ? start.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '--';
                const endLabel = endOrFallback ? endOrFallback.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '--';
                return `${startLabel} - ${endLabel}`;
              })()}
            </p>
            <p className="mt-1 text-xs text-slate-600">{appointment.provider.name}</p>
          </div>
        ))}
        {!appointments.length && <p className="text-sm text-slate-500">{translate('No data')}</p>}
      </div>
    </section>
  );
}

function HomeMetricCard({
  label,
  value,
  detail,
  icon,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  icon: React.ReactNode;
  tone: 'sky' | 'emerald' | 'amber' | 'slate';
}) {
  const toneClass = {
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  }[tone];

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-2 text-3xl font-semibold text-ink">{value}</p>
        </div>
        <div className={cn('rounded-lg border p-2', toneClass)}>{icon}</div>
      </div>
      <p className="mt-3 text-sm text-slate-600">{detail}</p>
    </div>
  );
}

function HomeTodayScheduleCard({
  appointments,
  translate,
  language,
}: {
  appointments: AppointmentRow[];
  translate: PhraseTranslator;
  language: SupportedLanguage;
}) {
  const locale = getLocale(language);
  const nextAppointments = appointments.filter((appointment) => appointment.status !== 'Cancelled' && appointment.status !== 'No-show').slice(0, 8);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink">{translate("Today's schedule")}</p>
          <p className="mt-1 text-xs text-slate-500">{translate('Next appointments and current visit status.')}</p>
        </div>
        <Link to="/appointments" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">
          {translate('Open appointments')}
          <ArrowRight size={15} />
        </Link>
      </div>
      <div className="mt-4 divide-y divide-slate-100">
        {nextAppointments.map((appointment) => {
          const start = parseSafeDate(appointment.start);
          const startLabel = start ? start.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '--';
          return (
            <div key={appointment.id} className="grid gap-3 py-3 sm:grid-cols-[80px_minmax(0,1fr)_140px] sm:items-center">
              <div className="text-sm font-semibold text-slate-900">{startLabel}</div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{appointment.patient}</p>
                <p className="truncate text-xs text-slate-500">{appointment.provider?.name || translate('Provider')}</p>
              </div>
              <div className="sm:text-right">
                <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
                  {translate(appointment.status)}
                </span>
              </div>
            </div>
          );
        })}
        {!nextAppointments.length ? <p className="py-5 text-sm text-slate-500">{translate('No appointments scheduled for today.')}</p> : null}
      </div>
    </section>
  );
}

function HomeProviderLoadCard({
  appointments,
  translate,
  language,
}: {
  appointments: AppointmentRow[];
  translate: PhraseTranslator;
  language: SupportedLanguage;
}) {
  const locale = getLocale(language);
  const rows = Array.from(
    appointments.reduce((map, appointment) => {
      const key = appointment.provider?.id || appointment.provider?.name || 'provider';
      const existing = map.get(key) ?? { name: appointment.provider?.name || translate('Provider'), count: 0, nextStart: null as Date | null };
      const start = parseSafeDate(appointment.start);
      existing.count += 1;
      if (start && (!existing.nextStart || start.getTime() < existing.nextStart.getTime())) existing.nextStart = start;
      map.set(key, existing);
      return map;
    }, new Map<string, { name: string; count: number; nextStart: Date | null }>()),
  )
    .map(([, value]) => value)
    .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name, locale))
    .slice(0, 6);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div>
        <p className="text-sm font-semibold text-ink">{translate('Provider load today')}</p>
        <p className="mt-1 text-xs text-slate-500">{translate('Visits grouped by clinician for today.')}</p>
      </div>
      <div className="mt-4 space-y-3">
        {rows.map((row) => (
          <div key={row.name} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <p className="truncate text-sm font-medium text-slate-900">{row.name}</p>
              <p className="text-sm font-semibold text-slate-900">{row.count.toLocaleString(locale)}</p>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {row.nextStart ? `${translate('Next')}: ${row.nextStart.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}` : translate('No next visit')}
            </p>
          </div>
        ))}
        {!rows.length ? <p className="text-sm text-slate-500">{translate('No provider visits scheduled today.')}</p> : null}
      </div>
    </section>
  );
}

function CommandCenterDayDispatchCard({
  data,
  translate,
  language,
  providerDirectory,
}: {
  data: DashboardState;
  translate: PhraseTranslator;
  language: SupportedLanguage;
  providerDirectory: ProviderDirectoryEntry[];
}) {
  const locale = getLocale(language);
  const today = useMemo(() => new Date(), []);
  const [selectedDayKey, setSelectedDayKey] = useState(() => toLocalDayKey(today));
  const selectedDate = useMemo(() => parseDayKey(selectedDayKey) ?? today, [selectedDayKey, today]);

  const dispatchStartHour = 7;
  const dispatchEndHour = 19;
  const timelineStart = useMemo(() => {
    const value = new Date(selectedDate);
    value.setHours(dispatchStartHour, 0, 0, 0);
    return value;
  }, [dispatchStartHour, selectedDate]);
  const timelineEnd = useMemo(() => {
    const value = new Date(selectedDate);
    value.setHours(dispatchEndHour + 1, 0, 0, 0);
    return value;
  }, [dispatchEndHour, selectedDate]);
  const timelineStartMs = timelineStart.getTime();
  const timelineEndMs = timelineEnd.getTime();
  const timelineDurationMs = timelineEndMs - timelineStartMs;
  const totalTimelineMinutes = Math.max(1, Math.round(timelineDurationMs / (60 * 1000)));
  const defaultProviderLabel = translate('Provider');

  const normalizeProviderName = (value?: string | null) =>
    String(value ?? '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();

  const dayAppointments = useMemo(() => {
    return data.scheduleAppointments
      .filter((appointment) => {
        const start = parseSafeDate(appointment.start);
        if (!start) return false;
        return toLocalDayKey(start) === selectedDayKey;
      })
      .sort((a, b) => {
        const left = parseSafeDate(a.start)?.getTime() ?? 0;
        const right = parseSafeDate(b.start)?.getTime() ?? 0;
        return left - right;
      });
  }, [data.scheduleAppointments, selectedDayKey]);

  type DispatchProviderRow = {
    id: string;
    providerId: string;
    name: string;
    normalizedName: string;
    avatarUrl?: string | null;
  };

  const providerRegistry = useMemo(() => {
    const rows = new Map<string, DispatchProviderRow>();
    const idToKey = new Map<string, string>();
    const nameToKey = new Map<string, string>();

    const upsertProvider = (candidate: { providerId?: string | null; name?: string | null; avatarUrl?: string | null }) => {
      const providerId = String(candidate.providerId ?? '').trim();
      const normalizedName = normalizeProviderName(candidate.name);
      const name = String(candidate.name ?? '').trim() || defaultProviderLabel;
      const avatarUrl = candidate.avatarUrl ?? null;

      let key = '';
      if (providerId && idToKey.has(providerId)) {
        key = idToKey.get(providerId)!;
      } else if (normalizedName && nameToKey.has(normalizedName)) {
        key = nameToKey.get(normalizedName)!;
      } else {
        key = providerId ? `id:${providerId}` : normalizedName ? `name:${normalizedName}` : `provider:${rows.size}`;
      }

      const existing = rows.get(key);
      const resolvedName = existing?.name && existing.name !== defaultProviderLabel ? existing.name : name;
      const resolvedProviderId = providerId || existing?.providerId || '';
      const resolvedNormalizedName = normalizedName || existing?.normalizedName || '';
      const resolvedAvatar = avatarUrl ?? existing?.avatarUrl ?? null;

      rows.set(key, {
        id: key,
        providerId: resolvedProviderId,
        name: resolvedName,
        normalizedName: resolvedNormalizedName,
        avatarUrl: resolvedAvatar,
      });

      if (resolvedProviderId) idToKey.set(resolvedProviderId, key);
      if (resolvedNormalizedName) nameToKey.set(resolvedNormalizedName, key);
    };

    providerDirectory.forEach((provider) => {
      upsertProvider({
        providerId: provider.id,
        name: provider.name,
        avatarUrl: provider.avatarUrl ?? null,
      });
    });

    data.providerWorkload?.buckets.forEach((bucket) => {
      const name = String(bucket.bucket ?? '').trim();
      if (!name) return;
      upsertProvider({ name });
    });

    data.scheduleAppointments.forEach((appointment) => {
      upsertProvider({
        providerId: appointment.provider?.id,
        name: appointment.provider?.name,
      });
    });

    const rowsSorted = Array.from(rows.values()).sort((a, b) => a.name.localeCompare(b.name, locale));
    return { rows: rowsSorted, idToKey, nameToKey };
  }, [data.providerWorkload, data.scheduleAppointments, defaultProviderLabel, locale, providerDirectory]);

  const providerRows = providerRegistry.rows;

  const appointmentsByProvider = useMemo(() => {
    const grouped = new Map<string, AppointmentRow[]>();
    dayAppointments.forEach((appointment) => {
      const providerId = String(appointment.provider?.id ?? '').trim();
      const normalizedName = normalizeProviderName(appointment.provider?.name);
      const key =
        (providerId ? providerRegistry.idToKey.get(providerId) : undefined) ??
        (normalizedName ? providerRegistry.nameToKey.get(normalizedName) : undefined);
      if (!key) return;
      const list = grouped.get(key) ?? [];
      list.push(appointment);
      grouped.set(key, list);
    });
    return grouped;
  }, [dayAppointments, providerRegistry]);

  const selectedDateLabel = selectedDate.toLocaleDateString(locale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  function shiftSelectedDate(deltaDays: number) {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + deltaDays);
    setSelectedDayKey(toLocalDayKey(next));
  }

  function formatBookedMinutes(minutes: number) {
    if (minutes <= 0) return `0m`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs <= 0) return `${mins}m`;
    if (mins <= 0) return `${hrs}h`;
    return `${hrs}h ${mins}m`;
  }

  return (
    <section className="card flex h-full min-h-[70vh] flex-col border border-slate-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink">{translate('Provider Day Dispatch')}</p>
          <p className="text-xs text-slate-500">{selectedDateLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => shiftSelectedDate(-1)}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
          >
            {translate('Prev')}
          </button>
          <input
            type="date"
            value={selectedDayKey}
            onChange={(event) => setSelectedDayKey(event.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700"
            aria-label={translate('Select date')}
          />
          <button
            type="button"
            onClick={() => shiftSelectedDate(1)}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
          >
            {translate('Next')}
          </button>
        </div>
      </div>

      <div className="mt-3 flex-1 rounded-xl border border-slate-200 bg-white">
        <div className="grid grid-cols-[minmax(220px,280px)_minmax(0,1fr)_120px] border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          <div className="border-r border-slate-200 px-3 py-2">{translate('Provider')}</div>
          <div className="border-r border-slate-200 px-3 py-2">{translate('Daily Load')}</div>
          <div className="px-3 py-2 text-right">{translate('Booked')}</div>
        </div>

        {providerRows.map((provider) => {
          const appointments = (appointmentsByProvider.get(provider.id) ?? []).sort((a, b) => {
            const left = parseSafeDate(a.start)?.getTime() ?? 0;
            const right = parseSafeDate(b.start)?.getTime() ?? 0;
            return left - right;
          });

          const visuals = appointments
            .map((appointment) => {
              const start = parseSafeDate(appointment.start);
              if (!start) return null;
              const end = parseSafeDate(appointment.end) ?? new Date(start.getTime() + 30 * 60 * 1000);
              const clampedStartMs = Math.max(start.getTime(), timelineStartMs);
              const clampedEndMs = Math.min(end.getTime(), timelineEndMs);
              if (clampedEndMs <= clampedStartMs || timelineDurationMs <= 0) return null;

              const leftPct = ((clampedStartMs - timelineStartMs) / timelineDurationMs) * 100;
              const widthPct = ((clampedEndMs - clampedStartMs) / timelineDurationMs) * 100;
              const toneClass =
                appointment.status === 'Cancelled'
                  ? 'bg-slate-400'
                  : appointment.status === 'No-show'
                    ? 'bg-rose-500'
                    : appointment.type === 'Telemed'
                      ? 'bg-sky-500'
                      : 'bg-emerald-500';
              const startLabel = start.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
              const endLabel = end.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
              const durationMin = Math.max(0, Math.round((clampedEndMs - clampedStartMs) / (60 * 1000)));

              return {
                appointment,
                toneClass,
                startLabel,
                endLabel,
                durationMin,
                leftPct,
                widthPct,
              };
            })
            .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

          const bookedMinutes = visuals.reduce((sum, entry) => sum + entry.durationMin, 0);
          const utilizationPct = Math.min(100, Math.round((bookedMinutes / totalTimelineMinutes) * 100));
          const initials =
            provider.name
              .split(' ')
              .filter(Boolean)
              .map((segment) => segment[0]?.toUpperCase() ?? '')
              .join('')
              .slice(0, 2) || 'PR';

          return (
            <div key={provider.id} className="grid grid-cols-[minmax(220px,280px)_minmax(0,1fr)_120px] border-b border-slate-100 last:border-b-0">
              <div className="border-r border-slate-100 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <InitialAvatar name={provider.name} src={provider.avatarUrl} alt={provider.name} className="h-8 w-8 text-[11px]" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-800">{provider.name}</p>
                    <p className="text-[11px] text-slate-500">
                      {appointments.length.toLocaleString(locale)} {translate('appointments')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-r border-slate-100 px-3 py-2.5">
                <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-slate-400">
                  <span>{timelineStart.toLocaleTimeString(locale, { hour: 'numeric' })}</span>
                  <span>{timelineEnd.toLocaleTimeString(locale, { hour: 'numeric' })}</span>
                </div>
                <div className="relative h-10 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                  <div className="absolute inset-y-0 left-0 bg-emerald-100/70" style={{ width: `${utilizationPct}%` }} />
                  {visuals.map((entry) => (
                    <div
                      key={entry.appointment.id}
                      className={cn('absolute top-1 h-8 rounded-md px-2 text-[10px] text-white shadow-sm', entry.toneClass)}
                      style={{
                        left: `${entry.leftPct}%`,
                        width: `${Math.max(entry.widthPct, 2)}%`,
                        minWidth: '42px',
                        maxWidth: `${Math.max(100 - entry.leftPct, 0)}%`,
                      }}
                      title={`${entry.appointment.patient} • ${entry.startLabel} - ${entry.endLabel}`}
                    >
                      <p className="truncate pt-[2px] font-semibold">{entry.appointment.patient}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="px-3 py-2.5 text-right">
                <p className="text-sm font-semibold text-slate-800">{utilizationPct}%</p>
                <p className="text-[11px] text-slate-500">{formatBookedMinutes(bookedMinutes)}</p>
              </div>
            </div>
          );
        })}
      </div>

      {!providerRows.length && <p className="mt-3 text-sm text-slate-500">{translate('No providers')}</p>}
      {!!providerRows.length && !dayAppointments.length && (
        <p className="mt-3 text-xs text-slate-500">{translate('No appointments on selected date')}</p>
      )}
    </section>
  );
}

function DashboardNotesCard({
  notes,
  translate,
  language,
}: {
  notes: ClinicalNoteRow[];
  translate: PhraseTranslator;
  language: SupportedLanguage;
}) {
  const locale = getLocale(language);
  const recentNotes = notes.slice(0, 3);
  return (
    <section className="card border border-slate-200 p-4">
      <p className="mb-3 text-3xl font-semibold text-ink">{translate('Notes')}</p>
      <div className="space-y-3">
        {recentNotes.map((note, index) => (
          <div key={note.id} className={cn('rounded-xl border px-3 py-2', index === 2 ? 'border-fuchsia-200 bg-fuchsia-50/70' : 'border-slate-200')}>
            <p className="text-sm font-semibold text-slate-800">{note.patient}</p>
            <p className="mt-1 text-xs text-slate-600">{note.summary}</p>
            <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-500">
              {new Date(note.createdAt).toLocaleDateString(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        ))}
        {!recentNotes.length && <p className="text-sm text-slate-500">{translate('No data')}</p>}
      </div>
    </section>
  );
}

function DashboardDailyAiSummaryCard({
  data,
  translate,
  language,
}: {
  data: DashboardState;
  translate: PhraseTranslator;
  language: SupportedLanguage;
}) {
  const locale = getLocale(language);
  const noShowAlerts = data.aiInsights.filter((insight) => insight.type === 'NoShowAlert');
  const highRisk = data.aiInsights.filter((insight) => insight.type === 'HighRiskPatient');
  const sourceNarrative = data.aiInsights.find((insight) => insight.type === 'DailySummary')?.payload.paragraph;
  const pendingLabs = data.kpis.find((kpi) => kpi.id === 'labs')?.value ?? 0;
  const openTasks = data.tasks.filter((task) => task.status !== 'Done').length;
  const upcomingCount = data.upcoming.length;

  const summaryText =
    sourceNarrative ||
    (language === 'es'
      ? `Hoy tienes ${upcomingCount.toLocaleString(locale)} citas próximas, ${openTasks.toLocaleString(locale)} tareas abiertas y ${pendingLabs.toLocaleString(locale)} laboratorios pendientes. Prioriza pacientes de riesgo alto y seguimiento preventivo.`
      : language === 'it'
        ? `Oggi hai ${upcomingCount.toLocaleString(locale)} prossimi appuntamenti, ${openTasks.toLocaleString(locale)} attività aperte e ${pendingLabs.toLocaleString(locale)} laboratori in sospeso. Dai priorità ai pazienti ad alto rischio e al follow-up preventivo.`
        : `Today you have ${upcomingCount.toLocaleString(locale)} upcoming appointments, ${openTasks.toLocaleString(locale)} open tasks, and ${pendingLabs.toLocaleString(locale)} pending labs. Prioritize high-risk patients and preventive follow-up.`);

  return (
    <section className="rounded-xl border border-sky-200 bg-sky-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-sky-700">
            <Sparkles size={14} /> {translate('AI Summary for Today')}
          </p>
          <p className="mt-1 text-sm text-slate-800">{summaryText}</p>
        </div>
        <span className="rounded-full border border-sky-300 bg-white px-3 py-1 text-xs font-semibold text-sky-900">{translate('Live')}</span>
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <div className="rounded-lg border border-slate-200 bg-white px-2 py-2">
          <p className="text-slate-500">{translate('No-show alerts')}</p>
          <p className="text-base font-semibold text-ink">{noShowAlerts.length.toLocaleString(locale)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-2 py-2">
          <p className="text-slate-500">{translate('High-risk patients')}</p>
          <p className="text-base font-semibold text-ink">{highRisk.length.toLocaleString(locale)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-2 py-2">
          <p className="text-slate-500">{translate('Pending labs')}</p>
          <p className="text-base font-semibold text-ink">{pendingLabs.toLocaleString(locale)}</p>
        </div>
      </div>
    </section>
  );
}

function StatusBadge({ status, translate }: { status: TaskRow['status']; translate: PhraseTranslator }) {
  const badgeClass =
    status === 'In Progress'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'Done'
        ? 'bg-blue-100 text-blue-700'
        : 'bg-fuchsia-100 text-fuchsia-700';
  return <span className={cn('inline-flex rounded-full px-3 py-1 text-xs font-semibold', badgeClass)}>{translate(status)}</span>;
}

function KpiCard({ kpi, translate, language }: { kpi: Kpi; translate: PhraseTranslator; language: SupportedLanguage }) {
  const locale = getLocale(language);
  const formatted =
    kpi.unit === '$'
      ? kpi.value.toLocaleString(locale, { style: 'currency', currency: getOrgCurrency(), maximumFractionDigits: 0 })
      : kpi.unit === '%'
        ? `${kpi.value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
        : kpi.value.toLocaleString(locale);
  const trendColor =
    kpi.trend?.direction === 'up' ? 'text-emerald-600' : kpi.trend?.direction === 'down' ? 'text-rose-600' : 'text-slate-500';
  const trendIcon = kpi.trend?.direction === 'up' ? '^' : kpi.trend?.direction === 'down' ? 'v' : '-';
  return (
    <div className="card p-4 space-y-2">
      <p className="text-xs uppercase tracking-wide text-slate-400">{translate(kpi.label)}</p>
      <div className="text-2xl font-semibold text-ink">{formatted}</div>
      {kpi.trend && (
        <div className={cn('text-xs', trendColor)}>
          {trendIcon} {Math.abs(kpi.trend.deltaPct).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% {translate('vs prior')}
        </div>
      )}
    </div>
  );
}

function HighchartsCard({ title, options }: { title: string; options: Highcharts.Options }) {
  return (
    <div className="card p-4 space-y-3 h-full flex flex-col overflow-hidden min-h-[280px]">
      <p className="text-sm font-semibold text-ink">{title}</p>
      <div className="flex-1 min-h-[220px]">
        <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { height: '100%' } }} />
      </div>
    </div>
  );
}
function widgetDefs(
  data: DashboardState,
  translate: PhraseTranslator = (phrase) => phrase,
  language: SupportedLanguage = 'en',
  providerDirectory: ProviderDirectoryEntry[] = [],
): WidgetDef[] {
  const locale = getLocale(language);
  return [
    {
      id: 'providerDayDispatch',
      title: translate('Provider Day Dispatch'),
      w: 12,
      h: 22,
      render: () => <CommandCenterDayDispatchCard data={data} translate={translate} language={language} providerDirectory={providerDirectory} />,
    },
    {
      id: 'revenue',
      title: translate('Revenue'),
      w: 4, h: 6,
      render: () => (
        <HighchartsCard
          title={translate('Total Revenue')}
          options={buildAreaOptions('Revenue', data.revenueTrends, 'USD', translate)}
        />
      ),
    },
    {
      id: 'activePatients',
      title: translate('Active Patients'),
      w: 4, h: 6,
      render: () => (
        <HighchartsCard
          title={translate('Active Patients Trend')}
          options={buildAreaOptions('Patients', data.activePatients, 'Count', translate)}
        />
      ),
    },
    {
      id: 'noShow',
      title: translate('No-Show Rate'),
      w: 4, h: 6,
      render: () => (
        <HighchartsCard
          title={translate('No-Show Rate (Completed vs Missed)')}
          options={buildStackedOptions('No-show vs Completed', data.noShowVsCompleted, translate)}
        />
      ),
    },
    {
      id: 'apptVolume',
      title: translate('Appointment Volume'),
      w: 4, h: 6,
      render: () => (
        <HighchartsCard
          title={translate('Appointment Volume (Clinic vs Telemed)')}
          options={buildAreaOptions('Volume', data.appointmentVolume, 'Visits', translate)}
        />
      ),
    },
    {
      id: 'teleGauge',
      title: translate('Telemed Utilization'),
      w: 4, h: 6,
      render: () => (
        <HighchartsCard
          title={translate('Telemed Utilization Gauge')}
          options={buildSolidGaugeOptions(translate('Telemed Utilization'), getLatestValue(data.telemedUtil))}
        />
      ),
    },
    {
      id: 'aiInsights',
      title: translate('AI Assistant'),
      w: 4, h: 6,
      render: () => <AiInsightsCard insights={data.aiInsights} translate={translate} />
    },
    {
      id: 'teleStream',
      title: translate('Provider Workload'),
      w: 4, h: 6,
      render: () => (
        <HighchartsCard title={translate('Provider Workload Stream')} options={buildStreamOptions(data.providerWorkload, translate)} />
      ),
    },
    {
      id: 'billingAlerts',
      title: translate('Billing Alerts'),
      w: 4,
      h: 6,
      render: () => (
        <TableCard
          title={translate('Billing Alerts')}
          columns={['Claim', 'Amount', 'Status', 'Reason']}
          rows={data.billingAlerts.map((b) => [
            b.claimId,
            b.amount.toLocaleString(locale, { style: 'currency', currency: getOrgCurrency() }),
            b.status,
            b.reason,
          ])}
          translate={translate}
        />
      ),
    },
    {
      id: 'aiUsage',
      title: translate('AI Usage Trend'),
      w: 4, h: 6,
      render: () => (
        <HighchartsCard title={translate('AI Assistance Usage')} options={buildLineOptions('AI Assistance', data.aiUsage, translate)} />
      ),
    },
    {
      id: 'labTurnaround',
      title: translate('Lab Turnaround'),
      w: 4, h: 6,
      render: () => (
        <HighchartsCard
          title={translate('Lab Turnaround Time')}
          options={buildAreaOptions('Turnaround', data.labTurnaround, 'Days', translate)}
        />
      ),
    },
    {
      id: 'tasks',
      title: translate('Tasks & Follow-ups'),
      w: 4,
      h: 6,
      render: () => (
        <TableCard
          title={translate('Tasks & Follow-ups')}
          columns={['Title', 'Due', 'Owner', 'Status', 'Priority']}
          rows={data.tasks.map((t) => [
            t.title,
            new Date(t.due).toLocaleDateString(locale),
            t.owner.name,
            t.status,
            t.priority,
          ])}
          translate={translate}
        />
      ),
    },
    {
      id: 'telemedLogs',
      title: translate('Telemed Session Logs'),
      w: 4,
      h: 6,
      render: () => (
        <TableCard
          title={translate('Telemed Session Logs')}
          columns={['Patient', 'Provider', 'Duration', 'Quality', 'Drops']}
          rows={data.telemedLogs.map((t) => [
            t.patient,
            t.provider.name,
            `${t.durationMin} min`,
            t.telemetry.quality.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
            t.drops,
          ])}
          translate={translate}
        />
      ),
    },
    {
      id: 'donut',
      title: translate('Claims Donut'),
      w: 4, h: 6,
      render: () => <HighchartsCard title={translate('Claim Success vs Rejected (donut)')} options={buildDonutOptions(data.revenueTrends, translate)} />,
    },
  ];
}

function buildAreaOptions(
  title: string,
  series?: TimeSeries,
  yTitle = '',
  translate: PhraseTranslator = (phrase) => phrase,
): Highcharts.Options {
  const grouped = groupSeries(series, translate);
  return {
    chart: { type: 'areaspline', backgroundColor: 'transparent' },
    title: { text: undefined },
    colors: palette,
    xAxis: { type: 'datetime', labels: { style: { color: '#64748b' } } },
    yAxis: { title: { text: translate(yTitle), style: { color: '#0f172a' } } },
    legend: { enabled: true },
    series: grouped.map(([name, pts]) => ({
      type: 'areaspline',
      name,
      data: pts.map((p) => [new Date(p.ts).getTime(), p.value]),
      fillOpacity: 0.08,
    })),
    credits: { enabled: false },
  };
}

function buildLineOptions(title: string, series?: TimeSeries, translate: PhraseTranslator = (phrase) => phrase): Highcharts.Options {
  const grouped = groupSeries(series, translate);
  return {
    chart: { type: 'spline', backgroundColor: 'transparent' },
    title: { text: undefined },
    colors: palette,
    xAxis: { type: 'datetime', labels: { style: { color: '#64748b' } } },
    yAxis: { title: { text: translate(title) } },
    credits: { enabled: false },
    series: grouped.map(([name, pts]) => ({
      type: 'spline',
      name,
      data: pts.map((p) => [new Date(p.ts).getTime(), p.value]),
    })),
  };
}

function buildStackedOptions(
  title: string,
  stacked?: StackedBarSeries,
  translate: PhraseTranslator = (phrase) => phrase,
): Highcharts.Options {
  return {
    chart: { type: 'bar', backgroundColor: 'transparent' },
    title: { text: undefined },
    colors: palette,
    xAxis: { categories: stacked?.buckets.map((b) => translate(b.bucket)) ?? [] },
    yAxis: { title: { text: translate(title) } },
    plotOptions: { series: { stacking: 'normal' } },
    credits: { enabled: false },
    series: [
      {
        type: 'bar',
        name: translate(stacked?.label ?? 'Completed'),
        data: stacked?.buckets.map((b) => b.value) ?? [],
      },
    ],
  };
}

function buildStreamOptions(workload?: StackedBarSeries, translate: PhraseTranslator = (phrase) => phrase): Highcharts.Options {
  const providers = workload?.buckets.map((b) => b.bucket) ?? ['Dr. Lane', 'Dr. Nair', 'Nurse Nina'];
  const days = Array.from({ length: 7 }, (_, i) => translate(`Day ${i + 1}`));
  const series =
    providers.map((p, idx) => ({
      type: 'streamgraph' as const,
      name: translate(p),
      data: days.map((_, d) => (workload?.buckets[idx]?.value ?? 60) * (0.6 + Math.random() * 0.8) - d * 2),
    })) ?? [];
  return {
    chart: { type: 'streamgraph', marginBottom: 30, backgroundColor: 'transparent' },
    title: { text: undefined },
    colors: palette,
    xAxis: { categories: days, labels: { style: { color: '#64748b' } } },
    yAxis: { visible: false },
    credits: { enabled: false },
    legend: { enabled: false },
    series,
  };
}

function buildDonutOptions(series?: TimeSeries, translate: PhraseTranslator = (phrase) => phrase): Highcharts.Options {
  const totalRevenue = series?.points.filter((p) => p.series === 'Revenue').reduce((acc, p) => acc + p.value, 0) ?? 0;
  const rejected = series?.points.filter((p) => p.series === 'Claims Rejected').reduce((acc, p) => acc + p.value, 0) ?? 0;
  const success = Math.max(0, totalRevenue - rejected);
  return {
    chart: { type: 'pie', backgroundColor: 'transparent' },
    title: { text: undefined },
    colors: [palette[2], palette[4]],
    plotOptions: {
      pie: { innerSize: '60%', dataLabels: { enabled: true, format: '{point.name}: {point.percentage:.1f}%' } },
    },
    credits: { enabled: false },
    series: [
      {
        type: 'pie',
        name: translate('Claims'),
        data: [
          { name: translate('Success'), y: success },
          { name: translate('Rejected'), y: rejected },
        ],
      },
    ],
  };
}

function buildBarOptions(
  top?: Array<{ code: string; label: string; count: number }>,
  translate: PhraseTranslator = (phrase) => phrase,
): Highcharts.Options {
  return {
    chart: { type: 'column', backgroundColor: 'transparent' },
    title: { text: undefined },
    colors: palette,
    xAxis: { categories: top?.map((d) => translate(d.label)) ?? [] },
    yAxis: { min: 0, title: { text: translate('Frequency') } },
    credits: { enabled: false },
    series: [
      {
        type: 'column',
        name: translate('Diagnoses'),
        data: top?.map((d) => ({ y: d.count, name: translate(d.label) })) ?? [],
      },
    ],
  };
}

function buildPatternPieOptions(
  top?: Array<{ code: string; label: string; count: number }>,
  translate: PhraseTranslator = (phrase) => phrase,
): Highcharts.Options {
  const patterns = [
    { pattern: { path: { d: 'M 0 0 L 3 3', strokeWidth: 2 } } },
    { pattern: { path: { d: 'M 3 0 L 0 3', strokeWidth: 2 } } },
    { pattern: { path: { d: 'M 0 3 L 3 0', strokeWidth: 2 } } },
    { pattern: { path: { d: 'M 1.5 0 L 1.5 3', strokeWidth: 2 } } },
  ];
  return {
    chart: { type: 'pie', backgroundColor: 'transparent' },
    title: { text: undefined },
    plotOptions: { pie: { dataLabels: { enabled: true, format: '{point.name}: {point.percentage:.1f}%' } } },
    credits: { enabled: false },
    series: [
      {
        type: 'pie',
        name: translate('Dx share'),
        data:
          top?.map((d, idx) => ({
            name: translate(d.label),
            y: d.count,
            color: {
              pattern: {
                ...((patterns[idx % patterns.length] as any).pattern || {}),
                color: palette[idx % palette.length],
              } as any, // Cast to any to avoid strict Highcharts type issues with patterns
            },
          })) ?? [],
      },
    ],
  };
}

function buildSolidGaugeOptions(title: string, value: number): Highcharts.Options {
  const pct = Math.min(100, Math.max(0, value));
  return {
    chart: { type: 'solidgauge', backgroundColor: 'transparent' },
    title: { text: undefined },
    pane: {
      center: ['50%', '52%'],
      size: '100%',
      startAngle: 0,
      endAngle: 360,
      background: [{ backgroundColor: '#e2e8f0', innerRadius: '72%', outerRadius: '100%', shape: 'arc' }],
    },
    tooltip: { enabled: false },
    yAxis: {
      min: 0,
      max: 100,
      stops: [
        [0.3, palette[4]],
        [0.6, palette[1]],
        [1, palette[2]],
      ],
      lineWidth: 0,
      tickWidth: 0,
      labels: { enabled: false },
    },
    credits: { enabled: false },
    series: [
      {
        type: 'solidgauge',
        name: title,
        data: [pct],
        dataLabels: {
          y: -8,
          format: `<div style="text-align:center"><span style="font-size:20px;color:#0f172a">{y}%</span><br/><span style="font-size:12px;color:#64748b">${title}</span></div>`,
        },
      },
    ],
  };
}

function buildWordCloudOptions(
  top?: Array<{ code: string; label: string; count: number }>,
  translate: PhraseTranslator = (phrase) => phrase,
): Highcharts.Options {
  const points =
    top?.map((d) => ({
      name: translate(d.label),
      weight: d.count,
      color: palette[Math.floor(Math.random() * palette.length)],
    })) ?? [
      { name: translate('Hypertension'), weight: 50 },
      { name: translate('Televisit'), weight: 40 },
      { name: translate('AI Notes'), weight: 30 },
      { name: translate('Labs'), weight: 25 },
    ];
  return {
    chart: { backgroundColor: 'transparent' },
    title: { text: undefined },
    series: [
      {
        type: 'wordcloud',
        data: points,
        name: translate('Frequency'),
      } as any,
    ],
    credits: { enabled: false },
  };
}

function getLatestValue(series?: TimeSeries) {
  return series?.points.at(-1)?.value ?? 0;
}

function groupSeries(series?: TimeSeries, translate: PhraseTranslator = (phrase) => phrase) {
  if (!series) return [] as Array<[string, TimeSeriesPoint[]]>;
  const groups: Record<string, TimeSeriesPoint[]> = {};
  series.points.forEach((p) => {
    const seriesName = translate(p.series);
    groups[seriesName] = groups[seriesName] ? [...groups[seriesName], p] : [p];
  });
  return Object.entries(groups);
}

function buildCommandCenterLayoutFromDefs(defs: WidgetDef[], visibleIds: string[]): LayoutItem[] {
  const defsById = new Map(defs.map((widget) => [widget.id, widget]));
  const template: Array<LayoutItem> = [
    { i: 'providerDayDispatch', x: 0, y: 0, w: 8, h: 16, minW: 6, minH: 12, size: 'lg' },
    { i: 'teleGauge', x: 8, y: 0, w: 2, h: 8, minW: 2, minH: 6, size: 'sm' },
    { i: 'donut', x: 10, y: 0, w: 2, h: 8, minW: 2, minH: 6, size: 'sm' },
    { i: 'aiInsights', x: 8, y: 8, w: 4, h: 8, minW: 4, minH: 6, size: 'sm' },
    { i: 'revenue', x: 0, y: 16, w: 4, h: 8, minW: 4, minH: 6, size: 'sm' },
    { i: 'apptVolume', x: 4, y: 16, w: 4, h: 8, minW: 4, minH: 6, size: 'sm' },
    { i: 'activePatients', x: 8, y: 16, w: 4, h: 8, minW: 4, minH: 6, size: 'sm' },
    { i: 'noShow', x: 0, y: 24, w: 4, h: 8, minW: 4, minH: 6, size: 'sm' },
    { i: 'teleStream', x: 4, y: 24, w: 4, h: 8, minW: 4, minH: 6, size: 'sm' },
    { i: 'aiUsage', x: 8, y: 24, w: 4, h: 8, minW: 4, minH: 6, size: 'sm' },
    { i: 'labTurnaround', x: 0, y: 32, w: 4, h: 8, minW: 4, minH: 6, size: 'sm' },
    { i: 'billingAlerts', x: 4, y: 32, w: 4, h: 9, minW: 4, minH: 7, size: 'sm' },
    { i: 'tasks', x: 8, y: 32, w: 4, h: 9, minW: 4, minH: 7, size: 'sm' },
    { i: 'telemedLogs', x: 0, y: 41, w: 12, h: 9, minW: 6, minH: 7, size: 'lg' },
  ];

  const positioned = template
    .filter((item) => visibleIds.includes(item.i) && defsById.has(item.i))
    .map((item) => {
      const widget = defsById.get(item.i)!;
      return {
        ...item,
        minW: item.minW ?? widget.minW ?? 4,
        minH: item.minH ?? widget.minH ?? 8,
      };
    });

  const positionedIds = new Set(positioned.map((item) => item.i));
  const fallbackDefs = defs.filter((widget) => visibleIds.includes(widget.id) && !positionedIds.has(widget.id));

  let cursorX = 0;
  let cursorY = positioned.reduce((max, item) => Math.max(max, item.y + item.h), 0);

  for (const widget of fallbackDefs) {
    const width = widget.w ?? 6;
    const height = widget.h ?? 10;
    if (cursorX + width > 12) {
      cursorX = 0;
      cursorY += 10;
    }
    positioned.push({
      i: widget.id,
      x: cursorX,
      y: cursorY,
      w: width,
      h: height,
      minW: widget.minW ?? 4,
      minH: widget.minH ?? 8,
      size: widget.size ?? 'md',
    });
    cursorX += width;
  }

  return positioned;
}

function buildDefaultLayoutFromDefs(defs: WidgetDef[], visibleIds: string[]): LayoutItem[] {
  const filteredDefs = defs.filter((w) => visibleIds.includes(w.id));
  let currentX = 0;
  let currentY = 0;

  return filteredDefs.map((w) => {
    if (currentX + (w.w ?? 6) > 12) {
      currentX = 0;
      currentY += 10;
    }

    const item: LayoutItem = {
      i: w.id,
      x: currentX,
      y: currentY,
      w: w.w ?? 6,
      h: w.h ?? 10,
      minW: w.minW ?? 4,
      minH: w.minH ?? 8,
      size: w.size ?? 'md',
    };

    currentX += item.w;
    return item;
  });
}

function TableCard({
  title,
  columns,
  rows,
  translate,
}: {
  title: string;
  columns: string[];
  rows: (string | number)[][];
  translate: PhraseTranslator;
}) {
  return (
    <div className="card flex h-full min-h-0 flex-col p-4">
      <p className="mb-3 text-sm font-semibold text-ink">{title}</p>
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500">
              {columns.map((c) => (
                <th key={c} className="py-2 pr-3">
                  {translate(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, idx) => (
              <tr key={idx}>
                {r.map((val, i) => (
                  <td key={i} className="py-2 pr-3 align-top text-slate-700">
                    {typeof val === 'string' ? translate(val) : val}
                  </td>
                ))}
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td className="py-3 text-slate-500" colSpan={columns.length}>
                  {translate('No data')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AiInsightsCard({ insights, translate }: { insights: AiInsight[]; translate: PhraseTranslator }) {
  const noShows = insights.filter((i) => i.type === 'NoShowAlert');
  const highRisk = insights.filter((i) => i.type === 'HighRiskPatient');
  const summary = insights.find((i) => i.type === 'DailySummary');
  const summaryPayload = summary?.payload as { paragraph?: string; text?: string } | undefined;
  const summaryText = summaryPayload?.paragraph ?? summaryPayload?.text;
  return (
    <div className="card p-4 space-y-3">
      <p className="text-sm font-semibold text-ink flex items-center gap-2">
        <Brain size={16} className="text-sky-500" /> {translate('AI Insights')}
      </p>
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-slate-400">{translate('Predictive no-show alerts')}</p>
        {noShows.map((n) => (
          <div key={n.id} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-slate-700">
              <CalendarClock size={14} className="text-amber-500" /> {n.payload.patient}
            </div>
            <span className="pill pill-warn">{Math.round(n.payload.probability * 100)}% {translate('risk')}</span>
          </div>
        ))}
        {!noShows.length && <p className="text-slate-500 text-sm">{translate('No alerts')}</p>}
      </div>
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-slate-400">{translate('High-risk patients')}</p>
        {highRisk.map((h) => (
          <div key={h.id} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-slate-700">
              <AlertTriangle size={14} className="text-rose-500" /> {h.payload.patient}
            </div>
            <span className="pill pill-danger">{Math.round(h.payload.riskScore * 100)}% {translate('risk')}</span>
          </div>
        ))}
        {!highRisk.length && <p className="text-slate-500 text-sm">{translate('None flagged')}</p>}
      </div>
      <div className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-slate-400">{translate('Daily summary')}</p>
        <p className="text-sm text-slate-700 leading-snug">
          {summaryText ? translate(summaryText) : translate('Summary not available.')}
        </p>
      </div>
    </div>
  );
}

function AiOverviewCard({
  data,
  translate,
  language,
}: {
  data: DashboardState;
  translate: PhraseTranslator;
  language: SupportedLanguage;
}) {
  const locale = getLocale(language);
  const getKpiValue = (id: string) => data.kpis.find((k) => k.id === id)?.value ?? 0;

  const patientsToday = getKpiValue('patients');
  const teleVisits = getKpiValue('teleVisits');
  const noShowRate = getKpiValue('noShow');
  const pendingLabs = getKpiValue('labs');
  const openTasks = data.tasks.filter((task) => task.status !== 'Done').length;
  const billingAlerts = data.billingAlerts.length;
  const highRiskCount = data.aiInsights.filter((insight) => insight.type === 'HighRiskPatient').length;
  const noShowAlerts = data.aiInsights.filter((insight) => insight.type === 'NoShowAlert').length;

  let loadScore = 0;
  if (patientsToday >= 90) loadScore += 2;
  else if (patientsToday >= 60) loadScore += 1;
  if (pendingLabs >= 20) loadScore += 2;
  else if (pendingLabs >= 10) loadScore += 1;
  if (noShowRate >= 10) loadScore += 1;
  if (billingAlerts >= 4) loadScore += 1;
  if (openTasks >= 8) loadScore += 1;
  if (highRiskCount >= 2) loadScore += 1;

  const loadState =
    loadScore >= 6
      ? { label: translate('High Load'), className: 'pill pill-danger' }
      : loadScore >= 3
        ? { label: translate('Moderate Load'), className: 'pill pill-warn' }
        : { label: translate('Stable Load'), className: 'pill pill-success' };

  const recommendation =
    loadScore >= 6
      ? (language === 'es'
        ? 'Priorizar seguimientos de alto riesgo y reequilibrar la carga de profesionales esta tarde.'
        : language === 'it' ? 'Dare priorità ai follow-up ad alto rischio e riequilibrare il carico di lavoro dei fornitori questo pomeriggio.' : 'Prioritize high-risk follow-ups and rebalance provider workload this afternoon.')
      : loadScore >= 3
        ? (language === 'es'
          ? 'Monitorear laboratorios pendientes y riesgo de ausencias; considerar alcance proactivo para visitas marcadas.'
          : language === 'it' ? 'Monitorare i laboratori in sospeso e il rischio di mancate presentazioni; considerare azioni proattive per le visite segnalate.' : 'Monitor pending labs and no-show risk; consider proactive outreach for flagged visits.')
        : (language === 'es'
          ? 'La carga actual de la clinica es estable; continuar con la rutina y monitorear alertas.'
          : language === 'it' ? 'Il carico attuale della clinica è stabile; continuare con la routine e monitorare gli avvisi.' : 'Current clinic load is stable; continue routine cadence and monitor alerts.');

  const dailySummary = data.aiInsights.find((insight) => insight.type === 'DailySummary');
  const summaryPayload = dailySummary?.payload as { paragraph?: string; text?: string } | undefined;
  const sourceDailyInsight = summaryPayload?.paragraph ?? summaryPayload?.text;
  const fallbackDailyInsight =
    language === 'es'
      ? 'No se encontro una narrativa IA en el flujo de insights; este resumen se deriva de las senales actuales del panel.'
      : language === 'it' ? 'Non è stata trovata alcuna narrativa AI nel flusso di insight, quindi questo riepilogo deriva dai segnali attuali della dashboard.' : 'No AI narrative was found from the insight stream, so this summary is derived from current dashboard signals.';
  const generatedSpanishInsight = `Se registran ${noShowAlerts.toLocaleString(locale)} alertas de ausencias potenciales y ${highRiskCount.toLocaleString(locale)} pacientes de alto riesgo; mantener seguimiento clinico activo y priorizar casos sensibles.`;
  const generatedItalianInsight = `Si registrano ${noShowAlerts.toLocaleString(locale)} potenziali avvisi di mancata presentazione e ${highRiskCount.toLocaleString(locale)} pazienti ad alto rischio; mantenere attivo il follow-up clinico e dare priorità ai casi sensibili.`;
  const dailyInsight = sourceDailyInsight
    ? (language === 'es' ? generatedSpanishInsight : language === 'it' ? generatedItalianInsight : translate(sourceDailyInsight))
    : fallbackDailyInsight;

  const narrative =
    language === 'es'
      ? `La clinica opera actualmente con un perfil de ${loadState.label.toLowerCase()} segun la actividad de hoy, con aproximadamente ${patientsToday.toLocaleString(locale)} pacientes en tablero, incluyendo ${teleVisits.toLocaleString(locale)} visitas de telemedicina. Los indicadores operativos muestran ${pendingLabs.toLocaleString(locale)} flujos de laboratorio pendientes, ${openTasks.toLocaleString(locale)} tareas de seguimiento abiertas y ${billingAlerts.toLocaleString(locale)} alertas activas de facturacion, mientras el monitoreo predictivo rastrea ${noShowAlerts.toLocaleString(locale)} riesgos potenciales de ausencia y ${highRiskCount.toLocaleString(locale)} alertas de pacientes de alto riesgo. La tasa de ausencias esta cerca de ${noShowRate.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% y la recomendacion de IA es ${recommendation.charAt(0).toLowerCase()}${recommendation.slice(1)}`
      : language === 'it'
        ? `Attualmente la clinica opera con un profilo di carico ${loadState.label.toLowerCase()} in base all'attività odierna, con circa ${patientsToday.toLocaleString(locale)} pazienti in tabellone, incluse ${teleVisits.toLocaleString(locale)} visite di telemedicina. Gli indicatori operativi mostrano ${pendingLabs.toLocaleString(locale)} analisi di laboratorio in sospeso, ${openTasks.toLocaleString(locale)} attività di follow-up aperte e ${billingAlerts.toLocaleString(locale)} avvisi di fatturazione attivi, mentre il monitoraggio predittivo rileva ${noShowAlerts.toLocaleString(locale)} potenziali rischi di mancate presentazioni e ${highRiskCount.toLocaleString(locale)} segnalazioni di pazienti ad alto rischio. Il tasso di mancata presentazione si aggira intorno al ${noShowRate.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% e la raccomandazione dell'AI è di ${recommendation.charAt(0).toLowerCase()}${recommendation.slice(1)}`
        : `The clinic is currently operating at a ${loadState.label.toLowerCase()} profile based on today's activity, with approximately ${patientsToday.toLocaleString(locale)} patients on the board including ${teleVisits.toLocaleString(locale)} telemedicine visits. Operational pressure indicators show ${pendingLabs.toLocaleString(locale)} pending lab workflows, ${openTasks.toLocaleString(locale)} open follow-up tasks, and ${billingAlerts.toLocaleString(locale)} active billing alerts, while predictive monitoring is tracking ${noShowAlerts.toLocaleString(locale)} potential no-show risks and ${highRiskCount.toLocaleString(locale)} high-risk patient flags. The no-show rate is running near ${noShowRate.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%, and the AI recommendation is to ${recommendation.charAt(0).toLowerCase()}${recommendation.slice(1)}`;

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink flex items-center gap-2">
            <Brain size={16} className="text-sky-500" /> {translate('AI Overview')}
          </p>
          <p className="text-xs text-slate-500 mt-1">{translate('Rapid clinic status snapshot for today')}</p>
        </div>
        <span className={loadState.className}>{loadState.label}</span>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
        <p className="dashboard-ai-summary-text text-sm text-slate-700 leading-relaxed">{narrative}</p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <p className="text-xs uppercase tracking-wide text-slate-500">{translate('AI Daily Insight')}</p>
        <p className="dashboard-ai-summary-text text-sm text-slate-700 mt-1 leading-snug">{dailyInsight}</p>
      </div>
    </div>
  );
}
